import { Component, Inject, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FeatureFlag } from '@core/enums';
import { FieldDef } from 'app/core/models/account.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, ProjectTemplate } from 'app/modules/projects/projects.types';
import { hasValue, isEmptyOrWhitespace } from '@shared/Utils/common.utils';
import { AccountState } from '@state/account/state';

@Component({
  selector: 'project-status-required-fields',
  templateUrl: './project-status-required-fields.component.html',
  styleUrls: ['./project-status-required-fields.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectStatusRequiredFieldsComponent extends OnDestroyMixin implements OnInit {
  private readonly store = inject(Store);

  fieldDefs: FieldDef[] = [];
  project: Project;
  displayColumns: FieldDef[] = [];
  options: [];
  projectTemplates: ProjectTemplate<any>[];
  duplicateProjectId: number = 0;
  duplicateProjects: Project[] = [];
  uploadedFile: any;
  isLoading = false;
  isFieldLoading = true;

  protected readonly enforceRequiredFields = this.store.selectSnapshot(
    AccountState.isFeatureEnabled(FeatureFlag.ENFORCE_REQUIRED_FIELDS),
  );

  private readonly requiredFieldDefs = signal<FieldDef[]>([]);
  private readonly fieldValuesRevision = signal(0);

  protected readonly areRequiredFieldsComplete = computed(() => {
    this.fieldValuesRevision();

    if (!this.enforceRequiredFields) {
      return true;
    }

    return this.requiredFieldDefs().every((fieldDef) => this.isFieldFilled(this.project?.[fieldDef.name]));
  });

  get isStatus(): boolean {
    return this.data?.isStatus ?? true;
  }

  private get stageOrPhaseProperty(): string {
    return this.isStatus ? 'project_stage_id' : 'project_phase_id';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ProjectStatusRequiredFieldsComponent>,
    private _projectsService: ProjectsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fieldDefs = this.data.data.fieldDefs;
    this.project = this.data.project;
    if (this.project) {
      this.isFieldLoading = true;
      this._projectsService.bustProjectCache();
      this._projectsService
        .getProjectById(this.project.id)
        .pipe(untilComponentDestroyed(this))
        .subscribe((result) => {
          this.project = result;
          this.options = this.data.data;
          this.displayColumns = this.data.stage.required_fields
            .split(',')
            .map((field) => this.fieldDefs.find((def) => def.name === field))
            .filter((field): field is FieldDef => !!field);
          this.requiredFieldDefs.set(this.displayColumns);
          this.fieldValuesRevision.update((revision) => revision + 1);
          this.isFieldLoading = false;
        });
    }
  }

  onRequiredFieldUpdate(update?: Record<string, unknown>): void {
    if (update) {
      Object.assign(this.project, update);
    }

    this.fieldValuesRevision.update((revision) => revision + 1);
  }

  discard(): void {
    this.matDialogRef.close();
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef) => fieldDef.name == name);
  }

  update(): void {
    if (!this.areRequiredFieldsComplete()) {
      return;
    }

    this.isLoading = true;
    const data = this.displayColumns.reduce((p, c) => ({ ...p, [c.name]: this.project[c.name] }), {} as any);
    data.id = this.project.id;
    data[this.stageOrPhaseProperty] = this.data.stage.id;
    data[this.stageOrPhaseProperty.replace('_id', '')] = this.data.stage;
    this._projectsService
      .saveProject(data, this.data?.columns?.join(','))
      .pipe(untilComponentDestroyed(this))
      .subscribe((updatedProject) => {
        this.isLoading = false;
        this.matDialogRef.close({ updatedProject });
      });
  }

  cancel() {
    this.discard();
  }

  private isFieldFilled(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'string' || value == null) {
      return hasValue(value) && !isEmptyOrWhitespace(value as string);
    }

    return value !== null && value !== undefined;
  }
}
